import { apiClient } from '../api/client';

/**
 * Client-side usage events (see documentation/observability/usage-events.md).
 *
 * `track('notebooks', 'open', 'dashboard', { from: 'button' })` queues one event; the
 * queue is posted to `POST /events` by batches of 20 or every 10 s, and flushed with a
 * keepalive request when the tab is hidden or closed. Best effort by design: a failed
 * batch is dropped, never retried, and nothing here can break the page.
 *
 * The (feature, action) pairs are declared in the backend catalogue; an unknown pair is
 * refused in local (422, visible in the console) and silently dropped elsewhere.
 */
export type TrackProps = Record<string, string | number | boolean | null | undefined>;

type QueuedEvent = { feature: string; action: string; surface?: string; ts: string; props: TrackProps };

const BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 10_000;

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

const API_URL = import.meta.env.VITE_API_URL || '/api';

function post(events: QueuedEvent[], keepalive: boolean): Promise<unknown> {
  if (events.length === 0) return Promise.resolve();
  if (keepalive) {
    const token = localStorage.getItem('access_token');
    return fetch(`${API_URL}/events`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ events }),
    }).catch(() => undefined);
  }
  return apiClient.instance.post('/events', { events }).catch(() => undefined);
}

/** Send what is queued now. `keepalive` is for pagehide, when a normal request may be cancelled. */
export function flush(keepalive = false): Promise<unknown> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const batch = queue.splice(0, BATCH_SIZE);
  const sent = post(batch, keepalive);
  if (queue.length > 0) return sent.then(() => flush(keepalive));
  return sent;
}

function listen() {
  if (listening || typeof window === 'undefined' || typeof document === 'undefined') return;
  listening = true;
  const onHide = () => {
    if (document.visibilityState === 'hidden') void flush(true);
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', () => void flush(true));
}

export function track(feature: string, action: string, surface?: string, props: TrackProps = {}): void {
  try {
    if (!localStorage.getItem('access_token')) return; // nobody signed in: nothing to attribute
    listen();
    queue.push({ feature, action, surface, ts: new Date().toISOString(), props });
    if (queue.length >= BATCH_SIZE) {
      void flush();
    } else if (!timer) {
      timer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
    }
  } catch {
    /* tracking must never throw into the UI */
  }
}

/** Tests only. */
export function _resetForTests(): void {
  queue = [];
  if (timer) clearTimeout(timer);
  timer = null;
}
